import React from "react";
import { Button, Card, Input, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import { generateOptionId } from "../gameTypes";
import TtsAudioControls from "../TtsAudioControls";
import OptionRichText from "../OptionRichText";

const stripHtml = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

type Props = {
  game: Extract<Game, { type: "matching" }>;
  onChange: (game: Extract<Game, { type: "matching" }>) => void;
  generateAudio?: (text: string) => Promise<string>;
};

const MatchingEditor: React.FC<Props> = ({ game, onChange, generateAudio }) => {
  // Defensive: partial/legacy games may arrive without a pairs array.
  // Fall back to an empty list so the map() below doesn't crash.
  const pairs: typeof game.pairs = Array.isArray(game.pairs)
    ? game.pairs
    : [];

  const updatePair = (
    pairId: string,
    side: "left" | "right",
    patch: Partial<(typeof game.pairs)[number]["left"]>,
  ) => {
    onChange({
      ...game,
      pairs: pairs.map((p) =>
        p.id === pairId ? { ...p, [side]: { ...p[side], ...patch } } : p,
      ),
    });
  };

  const addPair = () => {
    onChange({
      ...game,
      pairs: [
        ...pairs,
        {
          id: generateOptionId(),
          left: { text: "", image: "" },
          right: { text: "", image: "" },
        },
      ],
    });
  };

  const removePair = (pairId: string) => {
    onChange({ ...game, pairs: pairs.filter((p) => p.id !== pairId) });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>
      <div>
        <label>Imagen (opcional)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url })}
        />
      </div>
      <div>
        <label>Pares</label>
        {pairs.map((pair) => (
          <Card key={pair.id} size="small" style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <strong>Izquierda</strong>
                <div
                  style={{
                    display: "flex",
                    gap: 4,
                    alignItems: "flex-start",
                    marginTop: 4,
                  }}
                >
                  <OptionRichText
                    value={pair.left.text || ""}
                    onChange={(html) =>
                      updatePair(pair.id, "left", { text: html })
                    }
                    placeholder="Texto"
                  />
                  <TtsAudioControls
                    text={stripHtml(pair.left.text || "")}
                    audioUrl={pair.left.audio}
                    onAudioChange={(url) =>
                      updatePair(pair.id, "left", { audio: url })
                    }
                    generateAudio={generateAudio}
                  />
                </div>
                <div style={{ marginTop: 8 }}>
                  <ImageUploadInput
                    value={pair.left.image}
                    onChange={(url) => updatePair(pair.id, "left", { image: url })}
                  />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <strong>Derecha</strong>
                <div
                  style={{
                    display: "flex",
                    gap: 4,
                    alignItems: "flex-start",
                    marginTop: 4,
                  }}
                >
                  <OptionRichText
                    value={pair.right.text || ""}
                    onChange={(html) =>
                      updatePair(pair.id, "right", { text: html })
                    }
                    placeholder="Texto"
                  />
                  <TtsAudioControls
                    text={stripHtml(pair.right.text || "")}
                    audioUrl={pair.right.audio}
                    onAudioChange={(url) =>
                      updatePair(pair.id, "right", { audio: url })
                    }
                    generateAudio={generateAudio}
                  />
                </div>
                <div style={{ marginTop: 8 }}>
                  <ImageUploadInput
                    value={pair.right.image}
                    onChange={(url) =>
                      updatePair(pair.id, "right", { image: url })
                    }
                  />
                </div>
              </div>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removePair(pair.id)}
              />
            </div>
          </Card>
        ))}
        <div style={{ marginTop: 8 }}>
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            block
            onClick={addPair}
          >
            Agregar par
          </Button>
        </div>
      </div>
    </Space>
  );
};

export default MatchingEditor;
